import {
	AppBoxWrap,
	EnergyDiv,
	ToolDiv,
	AppFlexBoxWrap,
} from './styles';
import ToolWithSilder from '../../components/ToolWithSlider.tsx';
import DisplayEnergy from '../../components/DisplayEnergy';
import Loader, { Loader_Type } from '../../components/Loader';
import { PARENT_EVENTS, SOlAR_QOUTE_APP_NAME, TOOL_RENDER_MODE } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useOrgTheme } from 'contexts/ThemeContext';
import ToolDimensionsAndScaleProvider from 'contexts/ToolDimensionsAndScaleContext';
import { useEffect, useMemo, useRef } from 'react';
import DisplayRoofData from 'components/DisplayRoofData';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { RefProvider } from 'contexts/RefContext';
import { getRoofAttrsData } from 'components/DisplayRoofData/utils';
import ToolContainer from 'components/ToolContainer/ToolContainer';
import { toggleFullViewSidePanel } from 'store/slices/ToolSlice';
import { debounce } from '../../util';
import { InteractionBlocker } from 'components/InteractionBlocker/interactionBlockerLayer';
import FileUploadLoader from 'components/CustomMapSelect/components/fileUploadLoader';
import { useWarmDsm } from 'hooks/useDsm';
import useAutoAddPanelOnLoad from 'hooks/useAutoAddPanelOnLoad';
import { selectFilteredGutterLines } from 'store/slices/RoofDataSlice/roofSelector';
import AutoDetectConfirmationPopup from 'components/tool/Editor/ButtonDiv/Facet/EdgeTool/AutoDetectConfirmationPopup';

export default function LoadRoofDesign() {
	const { data: currentRoofState, loading, reqDesignType } = useSelector((state: RootState) => state.roofData);
	useWarmDsm(currentRoofState.dsmUrl);
	const isApplyingParentPanel = useAutoAddPanelOnLoad();
	const { parentAppName } = useSelector((state: RootState) => state.QueryParams.data);
	const { fullScreenEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { org, allRoofSegs } = useSelector((state: RootState) => state.roofData.data);

	const filteredGutterLines = useSelector(selectFilteredGutterLines);
	const roofDesignData = useMemo(
		() => getRoofAttrsData(allRoofSegs, filteredGutterLines), [filteredGutterLines, allRoofSegs]);

	const { mainHeading } = useOrgTheme();
	const switchingImageryProvider = useQueriesRunningInBackGround([
		`switch-imagery-${currentRoofState.imagerySource}`,
		`switch-roof-imagery-${currentRoofState.imagerySource}`
	], 'mutating',
	);
	const isUserChangingDesignMode = useQueriesRunningInBackGround(
		[
			'switch-design-mode',
			'switch-roof-design-mode'
		],
		'mutating'
	);

	const dispatch = useDispatch();
	const notEnoughPanelsToMoveForward = currentRoofState.designType === 'SOLAR' &&
		currentRoofState.rasterSolarPanels.length < org.setting.minPanelsRequiredForDesignToQualify;

	const isValidDesignForB2C = currentRoofState.org.setting?.isB2C && (
		notEnoughPanelsToMoveForward ||
		(currentRoofState.designType === 'ROOF' && roofDesignData.totalAreaSqFt === 0)
	);
	const isFirstLoadHandled = useRef<boolean>(false);
	const toolContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(()=>{
		if(!loading && !isApplyingParentPanel && !isFirstLoadHandled.current && !isValidDesignForB2C){
			isFirstLoadHandled.current = true;
			setTimeout(() => {
				window.parent.postMessage({ id: PARENT_EVENTS.TOOL_LOADED }, '*');
			}, 300);
		}
	});

	useEffect(() => {
		const delay = fullScreenEnabled ? 1200 : 100;

		const debounceDispatch = debounce(() => {
			dispatch(toggleFullViewSidePanel({ enabled: fullScreenEnabled }));
		}, delay);

		debounceDispatch();
	}, [dispatch, fullScreenEnabled]);

	if (loading || isApplyingParentPanel || switchingImageryProvider || isUserChangingDesignMode ) {
		let type = Loader_Type.DEFAULT;
		if(org.setting?.isB2C && reqDesignType === 'SOLAR' ) type = Loader_Type.SLIDER;
		else if(!loading) type = Loader_Type.INDEFINITE;

		// This is the very first screen shown (before any design data loads), so unlike the
		// in-tool Loader usages it has no existing page content behind it to show through — it
		// needs its own solid, theme-aware background rather than inheriting whatever the
		// (possibly still-light) ancestor chain happens to have. var(--defaultBgColor) is the
		// same token index.css's .bodyWrapper/header use for this exact full-page background.
		return <Loader
			loaderText={loading ? 'loading' : ''}
			type={type}
			designType={reqDesignType}
			styles={{ background: 'var(--defaultBgColor)', height: '100vh', width: '100vw' }}
		/>;
	}

	if (isValidDesignForB2C) {
		window.parent.postMessage({ id: PARENT_EVENTS.DESIGN_HAS_LESS_THEN_REQUIRED_PANELS }, '*');
		return null;
	} else {
		window.parent.postMessage({ id: PARENT_EVENTS.DESIGN_HAS_SUFFICIENT_PANELS,
			detail: { maxPanels: currentRoofState.rasterSolarPanels.length } }, '*');
	}

	// if(toolRenderMode === TOOL_RENDER_MODE.BACKGROUND){
	// 	// TODO: implement auto save

	// 	// ............events to handle (discuss).............
	// 	// manual
	// 	// erroe
	// 	// panel
	// 	// autosave
	// 	// less than 3
	// }

	return (
		<div className="bodyWrap" >
			<AppBoxWrap fullScreenEnabled={fullScreenEnabled}>
				<div className='design-Container' >
					{!!mainHeading &&
						<div className='design-head'>
							<h3 className='title mobileview'>{mainHeading}</h3>
						</div>}
					<div className='design-body'>
						<RefProvider>
							<ToolDimensionsAndScaleProvider toolContainerRef={toolContainerRef}>
								<AppFlexBoxWrap fullScreenEnabled={fullScreenEnabled} isParentSQ={parentAppName === SOlAR_QOUTE_APP_NAME}>
									<InteractionBlocker>
										<ToolDiv fullScreenEnabled={fullScreenEnabled} isParentSQ={parentAppName === SOlAR_QOUTE_APP_NAME}>
											<ToolWithSilder />
										</ToolDiv>
									</InteractionBlocker>
									<EnergyDiv isParentSQ={parentAppName === SOlAR_QOUTE_APP_NAME} fullScreenEnabled={fullScreenEnabled}>
										<InteractionBlocker>
											<ToolContainer ref={toolContainerRef}/>
										</InteractionBlocker>
										{/* // Todo: use varibale from roofData state for tooltype to render layouts */}
										{currentRoofState.designType === 'ROOF' ? <DisplayRoofData/>:	<DisplayEnergy/>}
									</EnergyDiv>
									<AutoDetectConfirmationPopup/>
								</AppFlexBoxWrap>

								<FileUploadLoader/>
							</ToolDimensionsAndScaleProvider>
						</RefProvider>
					</div>
				</div>
			</AppBoxWrap>
		</div>
	);
}
